import { Component, computed, effect, OnDestroy, OnInit, signal, Signal, WritableSignal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { GenericHeaderComponent } from '@components/generic-header/generic-header.component';

import { CommonModule } from '@angular/common';
import { DisplaysService } from 'app/services/displays.service';
import { Router } from '@angular/router';
import { catchError, debounceTime, filter, of, pipe, Subject, switchMap, takeUntil } from 'rxjs';
import { Display } from 'app/interfaces/display.interface';
import { DisplaysListComponent } from '@components/my-displays/displays-list/displays-list.component';
import { ButtonComponent } from '@components/button/button.component';
import { ApiService } from 'app/services/api.service';
import { PlatformService, PlatformName } from 'app/services/platform.service';
import { environment } from '../../../environments/environment';


@Component({
  selector: 'app-mydisplays',
  templateUrl: './mydisplays.component.html',
  styleUrl: './mydisplays.component.scss',
  standalone: true,
  imports: [TranslateModule, GenericHeaderComponent, CommonModule, DisplaysListComponent, ButtonComponent]
})
export class MydisplaysComponent implements OnInit, OnDestroy {

  public displays!: Signal<Display[]>;
  public hasNextPage!: Signal<boolean>;
  public loaded!: Signal<boolean>;
  public status: WritableSignal<string> = signal<string>('all');
  public page: WritableSignal<number> = signal<number>(0);
  public query: WritableSignal<string> = signal<string>('');

  public currentSales = signal<number>(0);
  public nextLevelSales = signal<number>(0);
  public currentLevelSales = signal<number>(0);
  public currentStatus: string = '';

  public availableActios: number = 0;
  public validatingActios: number = 0;

  private filters$ = new Subject<{status: string, page: number, query: string}>();
  private destroy$ = new Subject<void>();
  private previousPlatform: PlatformName | null = null;

  constructor(private displaysService: DisplaysService, private apiService: ApiService, private translateService: TranslateService, private router: Router, private platformService: PlatformService) {
    this.displays = this.displaysService.displays;
    this.hasNextPage = this.displaysService.hasNextPage;
    this.loaded = this.displaysService.loaded;

    // Efecto reactivo para cargar la lista de ventas al cambiar el estado, página o consulta
    effect(() => {
      this.filters$.next({
        status: this.status(),
        page: this.page(),
        query: this.query()
      });
    }, {
      allowSignalWrites: true // Permite que las señales se escriban dentro del efecto
    });
  }

  public ngOnInit(): void {
    if (!environment.webComponent) {
      this.previousPlatform = this.platformService.getPlatform();
      this.platformService.setContextualPlatform('DISPLAYS');
    }

    const userInfo = localStorage.getItem('userInfo');
    if (userInfo) {
      const { ytdSales, loyaltyLevel, country, availableActios, validatingActios } = JSON.parse(userInfo);
      this.currentSales.set(ytdSales);
      this.currentStatus = loyaltyLevel;
      this.availableActios = availableActios;
      this.validatingActios = validatingActios;

      this.apiService.getNextLoyaltyLevelRequirements(this.currentStatus, country).subscribe({
        next: (nextLevel: any) => {
          this.nextLevelSales.set(nextLevel.ytdSales ?? 0);
        },
        error: (error: any) => console.log(error)
      });

      this.apiService.getCurrentLoyaltyLevelRequirements(this.currentStatus, country).subscribe({
        next: (currentLevel: any) => this.currentLevelSales.set(currentLevel.ytdSales ?? 0),
        error: (error: any) => console.log('Error al pedir los requisitos del nivel actual:', error)
      })
    }


    this.filters$.pipe(
      debounceTime(500),
      switchMap(filters =>
        this.displaysService.loadDisplaysList(filters.status, filters.page, 10, 'id,desc', filters.query)
          .pipe(
            catchError(error => {
              console.error('Error loading displays:', error);
              return of([]); // Retorna array vacío en caso de error para continuar el stream
            })
          )
      ),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (displays: Display[]) => {
        console.log('Displays loaded:', displays);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.previousPlatform) {
      this.platformService.setContextualPlatform(this.previousPlatform);
    }
  }

  public navigateToRegisterDisplay(): void {
    this.router.navigate(['/register-display']);
  }

  public navigateToPolicy(): void {
    //TODO: configurar urls distintas por país
    window.open('https://static.cosentino.com/we.cosentino.com/display_policy_2024/ES/Elite%20We%20-%20Display%202024_IB_ES.pdf', '_blank'); //NOSONAR
  }

  public loadNextPage(): void {
    if (this.hasNextPage()) {
      this.page.update((page: number) => page + 1);
    }
  }
}
